import React, { useEffect, useState, useRef } from "react";
import MessageModal from "./MessageModal";
import { UserRole, MessageUser } from '@/types/message';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '../../utils/api';

interface User {
  id: string;
  name: string;
  role: UserRole;
}

interface Conversation {
  _id: string;
  participants: string[];
  lastMessage: string;
  updatedAt: Date;
  unreadCount?: number; // Simulated for now
}

interface MessagesListProps {
  user: User;
}

const MessagesList: React.FC<MessagesListProps> = ({ user }) => {
  const [conversations, setConversations] = useState<Conversation[] | null>([]);
  const [modalState, setModalState] = useState<{
    conv: Conversation | null;
    other: MessageUser | null;
  }>({ conv: null, other: null });
  const { token } = useAuth();
  const [participantNames, setParticipantNames] = useState<{ [id: string]: string }>({});
  const [hoveredConvId, setHoveredConvId] = useState<string | null>(null);
  const prevUnreadCounts = useRef<{ [convId: string]: number }>({});
  const notificationAudio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    notificationAudio.current = new window.Audio('/sounds/mixkit-confirmation-tone-2867.wav');
  }, []);

  useEffect(() => {
    if (!token || !user.id) return;
    api.get(`/messages/conversations?userId=${user.id}`)
      .then(res => res.data)
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setConversations(data.data);
        } else {
          console.error('Error: Expected array of conversations');
          setConversations([]);
        }
      })
      .catch(err => {
        console.error('Error fetching conversations:', err);
        setConversations([]);
      });
  }, [user.id, token]);

  useEffect(() => {
    if (!token || !conversations || conversations.length === 0 || !user.id) return;

    const fetchNames = async () => {
      const names: { [id: string]: string } = {};
      await Promise.all((conversations || []).map(async (conv) => {
        const otherId = conv.participants.find(id => id !== user.id);
        if (otherId && !participantNames[otherId]) {
          try {
            const res = await api.get(`/users/${otherId}`);
            const data = res.data;
            names[otherId] = (data.firstName || '') + (data.lastName ? ' ' + data.lastName : '');
          } catch {
            names[otherId] = 'Unknown User';
          }
        }
      }));
      setParticipantNames(prev => ({ ...prev, ...names }));
    };

    fetchNames();
    // eslint-disable-next-line
  }, [conversations, token]);

  // Debug: Log conversations on each render
  useEffect(() => {
    console.log('Conversations:', conversations);
  }, [conversations]);

  // Poll conversations every 3 seconds to keep unread counts up to date
  useEffect(() => {
    if (!token || !user.id) return;
    const fetchConversations = () => {
      api.get(`/messages/conversations?userId=${user.id}`)
        .then(res => res.data)
        .then(data => {
          if (data.success && Array.isArray(data.data)) {
            // Play notification if any unread count increases
            const newUnread: { [convId: string]: number } = {};
            let shouldPlay = false;
            data.data.forEach((conv: any) => {
              const prev = prevUnreadCounts.current[conv._id] || 0;
              const curr = conv.unreadCount || 0;
              newUnread[conv._id] = curr;
              if (curr > prev) shouldPlay = true;
            });
            if (shouldPlay && notificationAudio.current) {
              notificationAudio.current.currentTime = 0;
              notificationAudio.current.play();
            }
            prevUnreadCounts.current = newUnread;
            setConversations(data.data);
          }
        });
    };

    fetchConversations(); // Initial fetch
    const interval = setInterval(fetchConversations, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [user.id, token]);

  // Re-fetch conversations after modal is closed to update unread badge
  useEffect(() => {
    if (!modalState.conv && token && user.id) {
      api.get(`/messages/conversations?userId=${user.id}`)
        .then(res => res.data)
        .then(data => {
          if (data.success && Array.isArray(data.data)) {
            setConversations(data.data);
          }
        });
    }
  }, [modalState.conv, token, user.id]);

  const handleOpenConversation = async (conv: Conversation) => {
    // Mark as read in backend only if user.id is a string
    const userId = user.id ?? '';
    const conversationId = conv._id ? String(conv._id) : '';
    if (userId && conversationId && token) {
      await markConversationAsRead(conversationId, userId, token);
      // Re-fetch conversations to get updated unread counts
      api.get(`/messages/conversations?userId=${user.id}`)
        .then(res => res.data)
        .then(data => {
          if (data.success && Array.isArray(data.data)) {
            setConversations(data.data);
          }
        });
    }
    const otherId = conv.participants.find(id => id !== user.id) || "";
    try {
      const response = await api.get(`/users/${otherId}`);
      const userData = response.data;
      setModalState({
        conv,
        other: {
          id: otherId,
          name: (userData.firstName || userData.lastName)
            ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
            : userData.name || "Unknown User",
          role: 'student'
        }
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      setModalState({
        conv,
        other: {
          id: otherId,
          name: "Unknown User",
          role: 'student'
        }
      });
    }
  };

  // Clear hover state when modal is open
  useEffect(() => {
    if (modalState.conv) setHoveredConvId(null);
  }, [modalState.conv]);

  return (
    <div>
      <h3 style={{marginBottom:8}}>Your Conversations</h3>
      <ul style={{listStyle:'none',padding:0}}>
        {(Array.isArray(conversations) ? conversations : []).map(conv => {
          const otherId = conv.participants.find(id => id !== user.id) || "";
          const isHovered = hoveredConvId === conv._id;
          const unreadCount = conv.unreadCount || 0;
          return (
            <li key={conv._id} style={{marginBottom:8}}>
              <button 
                onClick={e => {
                  e.currentTarget.blur();
                  setHoveredConvId(null);
                  handleOpenConversation(conv);
                }}
                onMouseEnter={() => {
                  if (!modalState.conv) setHoveredConvId(conv._id);
                }}
                onMouseLeave={() => {
                  if (!modalState.conv) setHoveredConvId(null);
                }}
                style={{
                  background: isHovered ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid transparent',
                  borderRadius: 6,
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  transition: 'background 0.2s, box-shadow 0.2s',
                  outline: 'none',
                  position: 'relative',
                }}
              >
                <div style={{fontWeight:'bold', display: 'flex', alignItems: 'center'}}>
                  Conversation with {participantNames[otherId] || "Unknown User"}
                  {unreadCount > 0 && (
                    <span style={{
                      marginLeft: 8,
                      background: '#ef4444',
                      color: '#fff',
                      borderRadius: '50%',
                      minWidth: 20,
                      height: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      padding: '0 6px'
                    }}>
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div style={{fontSize:12,color:'#888'}}>
                  Last: {conv.lastMessage || "No messages yet"}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {!Array.isArray(conversations) && (
        <div style={{ color: 'red', marginTop: 8 }}>
          Could not load conversations. Please try again later.
        </div>
      )}
      {modalState.conv && modalState.other && (
        <MessageModal
          key={modalState.conv._id}
          isOpen={!!modalState.conv}
          onClose={() => setModalState({ conv: null, other: null })}
          sender={user as MessageUser}
          receiver={modalState.other}
          conversationId={modalState.conv._id}
        />
      )}
    </div>
  );
};

async function markConversationAsRead(conversationId: string, userId: string, token: string) {
  try {
    await api.post('/messages/mark-conversation-as-read', { conversationId, userId });
  } catch (err) {
    console.error('Failed to mark conversation as read', err);
  }
}

export default MessagesList; 